import { motion } from "framer-motion";
import {
  Brain, Users, Target, Sparkles, TrendingUp, Lightbulb, Rocket,
  CheckCircle2, Star, Heart, GraduationCap, BookOpen, Compass, HandHeart,
} from "lucide-react";
import DiscQuadrant from "@/components/charts/DiscQuadrant";
import RiasecRadar from "@/components/charts/RiasecRadar";
import ScoreBar from "@/components/charts/ScoreBar";
import { CoBrandBar } from "@/components/admin/BrandDialog";

const Card = ({ children, className = "", testid }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 ${className}`} data-testid={testid}>{children}</div>
);

const SectionTitle = ({ icon: Icon, children, color = "#4338CA" }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: color + "1a" }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <h2 className="font-heading font-extrabold text-xl text-slate-900">{children}</h2>
  </div>
);

const Pill = ({ label, value }) => (
  <div className="px-3.5 py-2 rounded-xl bg-white/15">
    <p className="font-body text-[11px] text-indigo-100 uppercase tracking-wide">{label}</p>
    <p className="font-heading font-bold text-sm">{value}</p>
  </div>
);

const ReportContent = ({ data }) => {
  const { scores, report, goal, brand } = data;
  const cog = scores.cognitive, apt = scores.aptitude;
  const isStart = data.track === "start";
  const trackName = isStart ? "CareerCompass Start" : "CareerCompass Comeback";

  return (
    <div className="space-y-6 p-1">
      <div className="bg-white rounded-2xl border border-slate-200 px-5 py-3" data-testid="report-brand-bar">
        <CoBrandBar brand={brand} compact />
      </div>
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl bg-primary p-8 text-white relative overflow-hidden">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 mb-4" data-testid="report-track-badge">
          <Sparkles className="w-4 h-4" /><span className="font-body text-sm">{trackName} · Career Alignment Report</span>
        </div>
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl leading-tight max-w-2xl">
          {report.headline}
        </h1>
        <div className="flex flex-wrap gap-3 mt-6">
          <Pill label="DISC" value={`${scores.disc.primary_label}`} />
          <Pill label="Holland" value={scores.holland.code} />
          <Pill label="Cognitive" value={`${cog.percent}%`} />
          <Pill label="Aptitude" value={`${apt.percent}%`} />
        </div>
      </motion.div>

      {/* Confidence Booster / Starting strengths */}
      {(report.confidence_boost || report.starting_strengths) && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 sm:p-7" data-testid="confidence-boost">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <Heart className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="font-heading font-extrabold text-xl text-emerald-900">{report.starting_strengths ? "Your starting strengths" : "You've got this"}</h2>
          </div>
          <p className="font-body text-emerald-900/80 leading-relaxed">{report.starting_strengths || report.confidence_boost}</p>
        </motion.div>
      )}

      {/* Executive summary */}
      <Card testid="executive-summary">
        <SectionTitle icon={Sparkles}>Executive Summary</SectionTitle>
        <div className="font-body text-slate-600 leading-relaxed space-y-3">
          {(report.executive_summary || "").split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </Card>

      {/* Scores grid */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card testid="cognitive-card">
          <SectionTitle icon={Brain} color="#EF4444">Cognitive & Aptitude</SectionTitle>
          <ScoreBar label="Cognitive Ability" percent={cog.percent} color="#EF4444"
                    sub={`${cog.correct}/${cog.total} correct`} />
          <ScoreBar label="Aptitude" percent={apt.percent} color="#F59E0B"
                    sub={`${apt.correct}/${apt.total} correct`} />
          <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-x-4">
            {[...cog.categories, ...apt.categories].map((c) => (
              <ScoreBar key={c.name} label={c.name} percent={c.percent} color="#94A3B8" />
            ))}
          </div>
          {report.cognitive_insight && (
            <p className="font-body text-sm text-slate-500 mt-3 leading-relaxed">{report.cognitive_insight}</p>
          )}
        </Card>

        <Card testid="disc-card">
          <SectionTitle icon={Users} color="#10B981">DISC Personality</SectionTitle>
          <DiscQuadrant disc={scores.disc} />
          {report.personality_insight && (
            <p className="font-body text-sm text-slate-500 mt-4 leading-relaxed">{report.personality_insight}</p>
          )}
        </Card>
      </div>

      {/* Holland */}
      <Card testid="holland-card">
        <SectionTitle icon={Target} color="#3B82F6">Holland Interest Profile — {scores.holland.code}</SectionTitle>
        <div className="grid md:grid-cols-2 gap-6 items-center">
          <RiasecRadar holland={scores.holland} />
          <div>
            <div className="space-y-2">
              {scores.holland.scores.map((s) => (
                <ScoreBar key={s.type} label={`${s.type} · ${s.label}`} percent={s.percent} color="#3B82F6" />
              ))}
            </div>
          </div>
        </div>
        {report.interests_insight && (
          <p className="font-body text-sm text-slate-500 mt-4 leading-relaxed">{report.interests_insight}</p>
        )}
      </Card>

      {/* Course recommendations (Start track) */}
      {report.course_recommendations?.length > 0 && (
        <Card testid="course-recommendations">
          <SectionTitle icon={GraduationCap} color="#0891B2">Recommended Streams, Courses & Degrees</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            {report.course_recommendations.map((c, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-5" data-testid={`course-rec-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-heading font-bold text-slate-900">{c.title}</h3>
                  {c.level && <span className="font-body text-[11px] px-2 py-0.5 rounded-md bg-cyan-50 text-cyan-700 shrink-0">{c.level}</span>}
                </div>
                <p className="font-body text-sm text-slate-600 mt-2">{c.why}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Career matches */}
      {report.career_matches?.length > 0 && (
        <Card testid="career-matches">
          <SectionTitle icon={Star} color="#4338CA">{isStart ? "Career Paths to Explore" : "Best-fit Career Matches"}</SectionTitle>
          <div className="space-y-4">
            {report.career_matches.map((c, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-5" data-testid={`career-match-${i}`}>
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-heading font-bold text-slate-900">{c.title}</h3>
                  <span className="font-heading font-extrabold text-primary">{c.fit}% fit</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden my-2.5">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${c.fit}%` }} />
                </div>
                <p className="font-body text-sm text-slate-600">{c.why}</p>
                {c.entry_route && <p className="font-body text-xs text-slate-500 mt-2"><span className="font-semibold text-slate-700">How to get there:</span> {c.entry_route}</p>}
                {c.roles?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {c.roles.map((r, ri) => (
                      <span key={ri} className="font-body text-xs px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600">{r}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Strengths & development */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card testid="strengths-card">
          <SectionTitle icon={TrendingUp} color="#10B981">Your Strengths</SectionTitle>
          <ul className="space-y-2.5">
            {(report.strengths || []).map((s, i) => (
              <li key={i} className="flex gap-2.5 font-body text-sm text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{s}
              </li>
            ))}
          </ul>
        </Card>
        <Card testid="development-card">
          <SectionTitle icon={Lightbulb} color="#F59E0B">Areas to Develop</SectionTitle>
          <ul className="space-y-2.5">
            {(report.development_areas || []).map((s, i) => (
              <li key={i} className="flex gap-2.5 font-body text-sm text-slate-600">
                <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />{s}
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Plans */}
      {isStart ? (
        <>
          <div className="grid md:grid-cols-2 gap-6">
            <ListCard testid="focus-now-card" icon={BookOpen} color="#4338CA" title="Subjects & Skills to Focus on Now" items={report.focus_now} numbered />
            <ListCard testid="exposure-card" icon={Compass} color="#0891B2" title="Internship & Exposure Ideas" items={report.exposure_ideas} />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <ListCard testid="next12-card" icon={CheckCircle2} color="#3B82F6" title="Next 12 Months" items={report.next_12_months} />
            <ListCard testid="supporters-card" icon={HandHeart} color="#D946EF" title="For the Counsellor / Parent" subtitle="How to support this student — discussion prompts" items={report.for_supporters} />
          </div>
        </>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <ListCard testid="return-plan-card" icon={Rocket} color="#4338CA" title="Your Roadmap" items={report.returning_to_work_plan} numbered />
          <ListCard testid="next90-card" icon={CheckCircle2} color="#3B82F6" title="Next 90 Days" items={report.next_90_days} />
        </div>
      )}

      <p className="font-body text-xs text-slate-400 text-center pt-2">
        Generated by {trackName} · Goal: {goal || "career exploration"} · {new Date(data.created_at).toLocaleDateString()}
      </p>
    </div>
  );
};

const ListCard = ({ testid, icon, color, title, subtitle, items, numbered }) => (
  <Card testid={testid}>
    <SectionTitle icon={icon} color={color}>{title}</SectionTitle>
    {subtitle && <p className="font-body text-xs text-slate-500 -mt-2 mb-3">{subtitle}</p>}
    <ol className="space-y-2.5">
      {(items || []).map((s, i) => (
        <li key={i} className="flex gap-2.5 font-body text-sm text-slate-600">
          {numbered
            ? <span className="shrink-0 w-6 h-6 rounded-full font-heading font-bold text-xs flex items-center justify-center" style={{ background: color + "1a", color }}>{i + 1}</span>
            : <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color }} />}
          <span>{s}</span>
        </li>
      ))}
    </ol>
  </Card>
);

export default ReportContent;
